import { useCallback, useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PAIN } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import MemberCTA from "@/components/MemberCTA";

const fmt = (c) => `$${(c / 100).toFixed(2)}`;

export default function PainPool() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [claims, setClaims] = useState([]);
  const [amount, setAmount] = useState("1.00");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: d }, mine] = await Promise.all([
      api.get("/economics/dashboard"),
      user ? api.get("/pain-claims").then((r) => r.data).catch(() => []) : Promise.resolve([]),
    ]);
    setDash(d);
    setClaims(mine);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/pain-claims", {
        amount_cents: Math.round(parseFloat(amount) * 100),
        reason,
      });
      toast.success("Claim submitted");
      setReason("");
      setAmount("1.00");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Claim failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0c10] telesis-grain">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-4">
          <p className="telesis-eyebrow">Pain &amp; Suffering · 15% pool</p>
          <h1 className="text-3xl md:text-4xl font-black text-slate-100 tracking-tight">
            Compensation for what should not be in mentation
          </h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            Reserved monthly, per-capita claimable. Unused funds roll back into the Alternate Financier Pool at month end.
          </p>
          {dash && (
            <div className="telesis-panel rounded p-5 space-y-2 text-sm">
              <p>Pool this month: <b className="text-rose-300">{fmt(dash.pain_pool_cents)}</b></p>
              <p>Already claimed: <b>{fmt(dash.pain_pool_claimed_cents)}</b></p>
              <p>Remaining: <b className="text-emerald-300">{fmt(dash.pain_pool_remaining_cents)}</b></p>
              <p>Per-user monthly cap: <b>{fmt(dash.pain_per_user_cap_cents)}</b></p>
            </div>
          )}

          {user?.is_member ? (
            <form data-testid={PAIN.form} onSubmit={submit} className="telesis-panel rounded p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400">Amount (CAD)</label>
                <Input
                  data-testid={PAIN.amount}
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 font-mono"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400">Reason</label>
                <Textarea
                  data-testid={PAIN.reason}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  minLength={5}
                  rows={4}
                  className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 resize-none"
                />
              </div>
              <Button
                data-testid={PAIN.submit}
                disabled={busy}
                type="submit"
                className="w-full rounded-full bg-rose-500 hover:bg-rose-400 text-black font-semibold"
              >
                {busy ? "…" : "Submit claim"}
              </Button>
            </form>
          ) : (
            <MemberCTA label="Membership required to submit a claim" />
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <p className="telesis-eyebrow">Your claims</p>
          {claims.length === 0 && <p className="text-xs text-slate-500 font-mono">no claims yet</p>}
          {claims.map((c) => (
            <div key={c.claim_id} className="telesis-panel rounded p-4">
              <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">{c.month} · {c.status}</p>
              <p className="text-lg font-bold text-rose-300 mt-1">{fmt(c.amount_cents)}</p>
              <p className="text-xs text-slate-300 mt-1">{c.reason}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
